/**
 * Lights the sidebar section a page was opened from, when no section owns the page itself.
 *
 * Unpoly marks the sidebar link whose href or [up-alias] pattern matches the current
 * path (.up-current). A page reached from several sections matches none: a product is
 * opened from Tour Library, Marketplace, Seasons, Contracting and Bidding, and claiming
 * it for one would be wrong for the other four. Grid row links name the grid they were
 * opened from in `back=` (DataGrid::RowLinks), so the link that owns that grid is the
 * honest answer, and it gets ORIGIN_CLASS, painted like .up-current.
 *
 * The sidebar renders outside [up-main] and never re-renders, so this runs on every
 * location change rather than from the server. Whether a section owns the page is
 * Unpoly's call: it matches the whole URL, query string included, so this reads its
 * .up-current instead of matching again. Both hooks run after Unpoly has updated the nav:
 * it emits up:fragment:compile before running compilers, and its own
 * up:layer:location:changed listener was registered before this one.
 */
export const ORIGIN_CLASS = "sidebar-origin"

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

// Unpoly's pattern syntax: `*` matches anything, including slashes.
const patternMatches = (pattern, path) =>
  new RegExp(`^${pattern.split("*").map(escapeRegExp).join(".*")}$`).test(path)

const linkMatches = (link, path, origin = window.location.origin) => {
  const href = link.getAttribute("href")
  const patterns = (link.getAttribute("up-alias") || "").split(/\s+/).filter(Boolean)

  return (
    (href && new URL(href, origin).pathname === path) ||
    patterns.some((pattern) => patternMatches(pattern, path))
  )
}

// A full page under a tour (its cost analysis) carries the tour's grid as `tour_back=`
// instead, so its own Back returns to the tour (ToolsDropdown's tourBackParams).
const BACK_PARAMS = ["back", "tour_back"]

// The same-origin path `back=` (or `tour_back=`) names, if any.
export const backPath = (location, origin = window.location.origin) => {
  const params = new URL(location, origin).searchParams
  const back = BACK_PARAMS.map((name) => params.get(name)).find(Boolean)
  if (!back) {
    return null
  }

  const url = new URL(back, origin)
  return url.origin === origin ? url.pathname : null
}

export const markOrigin = (nav, location, origin = window.location.origin) => {
  nav.querySelectorAll(`.${ORIGIN_CLASS}`).forEach((link) => link.classList.remove(ORIGIN_CLASS))

  if (nav.querySelector("a.nav-link.up-current")) {
    return
  }

  const links = Array.from(nav.querySelectorAll("a.nav-link[href]"))
  const back = backPath(location, origin)
  const owner = back && links.find((link) => linkMatches(link, back, origin))

  if (owner) {
    owner.classList.add(ORIGIN_CLASS)
  }
}

const start = ({ up }) => {
  up.compiler("#sidebarMenu", (nav) => {
    markOrigin(nav, window.location.href)
  })

  up.on("up:layer:location:changed", (event) => {
    const nav = document.querySelector("#sidebarMenu")
    if (nav) {
      markOrigin(nav, event.location)
    }
  })
}

export default { start }
